import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import * as XLSX from 'xlsx';

const ImportPerformance = () => {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { filialeId, isConsolidated } = useFiliale();
  
  const [typePersonnel, setTypePersonnel] = useState(null);
  const [indicateurs, setIndicateurs] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [paies, setPaies] = useState([]);
  const [selectedPaie, setSelectedPaie] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [result, setResult] = useState(null);

  const fetchTypePersonnel = useCallback(async () => {
    try {
      const response = await http.get(`/api/types-personnel`);
      const allTypes = response.data || [];
      const type = allTypes.find(t => t.idtype_personnel === parseInt(typeId));
      setTypePersonnel(type);
    } catch (error) {
      console.error('Erreur récupération type personnel:', error);
    }
  }, [typeId]);

  const fetchIndicateurs = useCallback(async () => {
    try {
      const response = await http.get(`/api/indicateurs/type/${typeId}?withPond=true`);
      // NE PAS trier - utiliser l'ordre exact de la base de données
      setIndicateurs(response.data || []);
    } catch (error) {
      console.error('Erreur récupération indicateurs:', error);
      setIndicateurs([]);
    }
  }, [typeId]);

  const fetchPersonnel = useCallback(async () => {
    try {
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }
      
      const response = await http.get('/api/personnel', { params });
      // L'API retourne une structure paginée { data: [], total: X }
      const allPersonnel = Array.isArray(response.data?.data) ? response.data.data : [];
      console.log('Total personnel:', allPersonnel.length);
      console.log('Type ID recherché:', typeId);
      console.log('Type ID recherché (int):', parseInt(typeId));
      console.log('Filiale context:', filialeId, 'isConsolidated:', isConsolidated);
      
      const filtered = allPersonnel.filter(p => {
        const match = p.type_personnel_id === parseInt(typeId);
        if (!match && p.type_personnel_id) {
          console.log('Non match - Personnel:', p.nom, 'type_personnel_id:', p.type_personnel_id);
        }
        return match;
      });
      
      console.log('Personnel filtré:', filtered.length);
      
      // Ajouter un nom complet pour le template
      const personnelWithFullName = filtered.map(p => ({
        ...p,
        nom_complet: `${p.nom} ${p.prenom}`.trim()
      }));
      setPersonnel(personnelWithFullName);
    } catch (error) {
      console.error('Erreur récupération personnel:', error);
      setPersonnel([]);
    }
  }, [typeId, filialeId, isConsolidated]);

  const fetchPaies = useCallback(async () => {
    try {
      const params = {};
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.filiale_id = filialeId;
      }
      
      const response = await http.get('/api/paies', { params });
      const allPaies = response.data || [];
      
      // Sélectionner automatiquement la paie active (etat = 1) pour cette filiale
      const activePaie = allPaies.find(p => p.etat === 1);
      if (activePaie) {
        setSelectedPaie(activePaie.idpaie);
        setCurrentStep(1);
      } else {
        setPaies(allPaies);
        setCurrentStep(0); // Reste à l'étape de sélection si aucune paie active
      }
      
      setPaies(allPaies);
    } catch (error) {
      console.error('Erreur récupération paies:', error);
      setPaies([]);
    }
  }, [filialeId, isConsolidated]);

  const getPaieLabel = (paie) => {
    if (!paie) return '';
    let label = paie.libelle_paie;
    if (paie.filiale_libelle) {
      label += ` - ${paie.filiale_libelle}`;
    }
    return label;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTypePersonnel(),
        fetchIndicateurs(),
        fetchPersonnel(),
        fetchPaies()
      ]);
      setLoading(false);
    };
    loadData();
  }, [typeId, fetchTypePersonnel, fetchIndicateurs, fetchPersonnel, fetchPaies]);

  const generateTemplate = async () => {
    if (!selectedPaie) {
      alert('Veuillez sélectionner une paie');
      return;
    }

    try {
      setGenerating(true);
      setCurrentStep(2);

      // Créer le workbook
      const wb = XLSX.utils.book_new();
      
      // Préparer les données
      const wsData = [];
      
      // En-têtes - Utiliser l'ordre exact des indicateurs retournés par l'API
      const headers = ['ID Personnel', 'Nom Complet', 'Prénom', ...indicateurs.map(i => i.Libelle_indicateur)];
      console.log('Ordre des indicateurs dans le template:', indicateurs.map(i => ({ libelle: i.Libelle_indicateur, id: i.idindicateur_has_type_personnel })));
      wsData.push(headers);
      
      // Données du personnel
      personnel.forEach(person => {
        const row = [person.idpersonnel, person.nom, person.prenom];
        indicateurs.forEach(() => row.push(''));
        wsData.push(row);
      });
      
      // Créer la feuille
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Styliser les en-têtes
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({r: 0, c: col});
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "0D6EFD" } },
          color: { rgb: "FFFFFF" }
        };
      }
      
      // Ajuster la largeur des colonnes
      const colWidths = headers.map(header => ({wch: Math.max(header.length, 15)}));
      ws['!cols'] = colWidths;
      
      // Ajouter la feuille
      XLSX.utils.book_append_sheet(wb, ws, "Template Performances");
      
      // Télécharger
      const fileName = `template_performances_${typePersonnel?.libelle || 'type'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      setCurrentStep(3);
    } catch (error) {
      console.error('Erreur génération template:', error);
      alert('Erreur lors de la génération du template');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const importPerformance = async () => {
    if (!file) {
      alert('Veuillez sélectionner un fichier Excel');
      return;
    }

    if (!selectedPaie) {
      alert('Veuillez sélectionner une paie');
      return;
    }

    try {
      setImporting(true);
      setCurrentStep(3);

      // Lire le fichier Excel
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // header: 0 par défaut
      
      console.log('Données brutes Excel:', jsonData);
      console.log('Clés de la première ligne:', Object.keys(jsonData[0] || {}));
      console.log('Indicateurs disponibles:', indicateurs.map(i => ({ libelle: i.Libelle_indicateur, id: i.idindicateur_has_type_personnel })));
      
      // Créer un mapping libellé -> ID pour retrouver l'indicateur par son libellé
      const indicateurMap = {};
      indicateurs.forEach(ind => {
        indicateurMap[ind.Libelle_indicateur] = ind.idindicateur_has_type_personnel;
      });
      console.log('Mapping libellé -> ID:', indicateurMap);
      
      const performances = [];
      
      jsonData.forEach(row => {
        // Les clés sont "ID Personnel", "Nom Complet", "Prénom", puis les libellés des indicateurs
        const personnelId = row['ID Personnel'] || row['ID_Personnel'] || row['idpersonnel'] || row['ID'];
        console.log('Row personnelId:', personnelId, 'Type:', typeof personnelId);
        
        if (!personnelId) {
          console.log('Clés disponibles:', Object.keys(row));
          return;
        }
        
        const personnelItem = personnel.find(p => p.idpersonnel === parseInt(personnelId));
        console.log('PersonnelItem trouvé:', personnelItem ? 'OUI' : 'NON', 'pour ID:', personnelId);
        
        if (!personnelItem) return;

        // Mapper les indicateurs en utilisant le mapping libellé -> ID
        const performanceData = {
          personnel_id: parseInt(personnelId),
          paie_id: selectedPaie,
          valeurs: {}
        };

        // Parcourir toutes les clés de la ligne Excel
        Object.keys(row).forEach(key => {
          // Ignorer les colonnes fixes (ID Personnel, Nom Complet, Prénom)
          if (['ID Personnel', 'Nom Complet', 'Prénom'].includes(key)) return;
          
          // Trouver l'ID de l'indicateur correspondant à ce libellé
          const indicateurId = indicateurMap[key];
          if (indicateurId) {
            const value = row[key];
            console.log('Mapping:', key, '-> ID:', indicateurId, 'Valeur:', value);
            if (value !== undefined && value !== '' && value !== null) {
              performanceData.valeurs[indicateurId] = parseFloat(value) || 0;
            }
          } else {
            console.log('Aucun indicateur trouvé pour le libellé:', key);
          }
        });

        console.log('PerformanceData:', performanceData);
        performances.push(performanceData);
      });

      console.log('Performances à envoyer:', performances);

      // Envoyer au backend
      const response = await http.post('/api/performance/import', {
        paie_id: selectedPaie,
        type_personnel_id: typeId,
        performances: performances
      });

      setResult({
        success: true,
        message: `${performances.length} performances importées avec succès`,
        details: response.data.details || response.data
      });
      console.log('Réponse backend:', response.data);
    } catch (error) {
      console.error('Erreur import:', error);
      setResult({
        success: false,
        message: 'Erreur lors de l\'importation',
        error: error.message
      });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BlueHeader
          title="Importation Performances"
          subtitle="Chargement..."
          actions={[
            {
              label: 'Retour',
              icon: ArrowLeft,
              onClick: () => navigate('/import-indicateurs'),
              primary: false
            }
          ]}
        />
        <div className="flex h-screen items-center justify-center">
          <div className="text-lg text-gray-500">Chargement des données...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title={`Importation Performances : ${typePersonnel?.libelle || 'En chargement...'}`}
        subtitle="Importez les données de performance par type de personnel"
        actions={[
          {
            label: 'Retour',
            icon: ArrowLeft,
            onClick: () => navigate('/import-indicateurs'),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: () => { fetchIndicateurs(); fetchPersonnel(); fetchPaies(); },
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        {/* Étapes d'importation */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Étape 1 */}
            <div className={`border-2 rounded-xl p-6 text-center transition-all ${
              currentStep >= 1 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
            }`}>
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <FileSpreadsheet className="h-8 w-8" />
              </div>
              <h3 className={`font-semibold mb-2 ${currentStep >= 1 ? 'text-blue-900' : 'text-gray-600'}`}>
                Étape 1
              </h3>
              <p className="text-sm text-gray-600">Sélection de la paie</p>
            </div>

            {/* Étape 2 */}
            <div className={`border-2 rounded-xl p-6 text-center transition-all ${
              currentStep >= 2 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
            }`}>
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <Download className="h-8 w-8" />
              </div>
              <h3 className={`font-semibold mb-2 ${currentStep >= 2 ? 'text-blue-900' : 'text-gray-600'}`}>
                Étape 2
              </h3>
              <p className="text-sm text-gray-600">Génération du template</p>
            </div>

            {/* Étape 3 */}
            <div className={`border-2 rounded-xl p-6 text-center transition-all ${
              currentStep >= 3 ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
            }`}>
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                <Upload className="h-8 w-8" />
              </div>
              <h3 className={`font-semibold mb-2 ${currentStep >= 3 ? 'text-blue-900' : 'text-gray-600'}`}>
                Étape 3
              </h3>
              <p className="text-sm text-gray-600">Importation des données</p>
            </div>
          </div>
        </div>

        {/* Formulaire d'importation */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Paie Active
              </label>
              {selectedPaie ? (
                <input
                  type="text"
                  value={getPaieLabel(paies.find(p => p.idpaie === selectedPaie))}
                  disabled
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed"
                  readOnly
                />
              ) : (
                <select
                  value={selectedPaie || ''}
                  onChange={(e) => setSelectedPaie(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="">-- Sélectionnez une paie --</option>
                  {paies.map(paie => (
                    <option key={paie.idpaie} value={paie.idpaie}>
                      {getPaieLabel(paie)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fichier Excel
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={generateTemplate}
              disabled={generating}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="h-5 w-5" />
              {generating ? 'Génération...' : 'Générer Template Excel'}
            </button>
            <button
              onClick={importPerformance}
              disabled={importing || !file}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="h-5 w-5" />
              {importing ? 'Importation...' : 'Importer les Performances'}
            </button>
          </div>
        </div>

        {/* Informations */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex gap-4">
            <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Instructions pour l'importation</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                <li>La paie active est automatiquement sélectionnée</li>
                <li>Cliquez sur "Générer Template Excel" pour obtenir le modèle</li>
                <li>Remplissez le fichier Excel avec les données de performance</li>
                <li>Sélectionnez le fichier complété et cliquez sur "Importer les Performances"</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Résultat */}
        {result && (
          <div className={`rounded-xl p-6 ${
            result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex gap-4 mb-4">
              {result.success ? (
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
              )}
              <div>
                <h4 className={`font-semibold mb-2 ${result.success ? 'text-green-900' : 'text-red-900'}`}>
                  {result.success ? 'Importation réussie' : 'Erreur d\'importation'}
                </h4>
                <p className={`text-sm ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                  {result.message}
                </p>
              </div>
            </div>

            {/* Tableau récapitulatif */}
            {result.success && result.details && Array.isArray(result.details) && (
              <>
                {console.log('Affichage tableau - details:', result.details, 'indicateurs:', indicateurs)}
                <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h5 className="font-semibold text-gray-800">Récapitulatif de l'importation</h5>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                          {indicateurs.map(ind => (
                            <th key={ind.idindicateur_has_type_personnel} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              {ind.Libelle_indicateur}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {result.details.map((perf, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm">
                              {personnel.find(p => p.idpersonnel === perf.personnel_id)?.nom_complet || `Employé #${perf.personnel_id}`}
                            </td>
                            {indicateurs.map(ind => (
                              <td key={ind.idindicateur_has_type_personnel} className="px-4 py-2 text-sm text-center">
                                {perf.valeurs[String(ind.idindicateur_has_type_personnel)] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-1">Indicateurs</div>
            <div className="text-2xl font-bold text-gray-900">{indicateurs.length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-1">Personnel</div>
            <div className="text-2xl font-bold text-gray-900">{personnel.length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-1">Paies actives</div>
            <div className="text-2xl font-bold text-gray-900">{paies.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportPerformance;
